import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, Plus } from 'lucide-react';

import type { SchemaDefinition } from '../../lib/api';
import { getCategoryLabel } from './helpers';
import styles from './schema-editor.module.css';

interface SchemaSwitcherProps {
  schemas: SchemaDefinition[];
  selectedSchemaId: string;
  onSelect: (schemaId: string) => void;
  onDeselect: () => void;
  onCreate: () => void;
}

/** Short "{Category} · v{n}" caption shown under each schema name. */
function schemaCaption(schema: SchemaDefinition): string {
  const version = schema.versionNumber ?? schema.version_number;
  const category = getCategoryLabel(schema.category);
  return version != null ? `${category} · v${version}` : category;
}

/**
 * Top-of-editor schema selector: a button showing the active schema's name that
 * opens a dropdown of every schema, plus "back to all schemas" and "create"
 * actions. Replaces the former persistent left sidebar.
 */
export const SchemaSwitcher: React.FC<SchemaSwitcherProps> = ({
  schemas,
  selectedSchemaId,
  onSelect,
  onDeselect,
  onCreate,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close the dropdown on outside click or Escape — no render-time equivalent.
  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const activeSchema = schemas.find(schema => schema.id === selectedSchemaId);
  const activeName = activeSchema?.name ?? 'Select a schema';

  return (
    <div ref={containerRef} className={styles.switcher}>
      <button
        type="button"
        className={styles.switcherTrigger}
        data-open={open}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(previous => !previous)}>
        <span className={styles.switcherTriggerName}>{activeName}</span>
        <ChevronDown size={16} className={styles.switcherChevron} />
      </button>

      {open && (
        <div className={styles.switcherMenu}>
          <div className={styles.switcherMenuHeader}>
            <span className={styles.switcherEyebrow}>Schemas</span>
            <span className={styles.switcherCount}>{schemas.length}</span>
          </div>

          <button
            type="button"
            className={styles.switcherAllSchemas}
            onClick={() => {
              onDeselect();
              setOpen(false);
            }}>
            <ArrowLeft size={14} />
            All schemas
          </button>

          <div className={styles.switcherList}>
            {schemas.map(schema => {
              const selected = schema.id === selectedSchemaId;
              return (
                <button
                  key={schema.id}
                  type="button"
                  data-active={selected}
                  className={styles.switcherItem}
                  onClick={() => {
                    onSelect(schema.id);
                    setOpen(false);
                  }}>
                  <span className={styles.switcherItemText}>
                    <span className={styles.switcherItemName}>{schema.name}</span>
                    <span className={styles.switcherItemSub}>{schemaCaption(schema)}</span>
                  </span>
                  {selected && <Check size={14} className={styles.switcherItemCheck} />}
                </button>
              );
            })}
          </div>

          <div className={styles.switcherFooter}>
            <button
              type="button"
              className={styles.switcherCreateButton}
              onClick={() => {
                onCreate();
                setOpen(false);
              }}>
              <Plus size={14} />
              Create schema
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
